import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ASSETS_BY_IDS } from '@scio3/graphql-client';
import type { Asset } from '@scio3/shared-types';
import { ASSET_STATUS } from '@scio3/shared-types';
import { useTeamStore } from '@/stores/team-store';

const POLL_INTERVAL_MS = 5_000;
/**
 * Max poll retries after CONVERTING → ACTIVE with no thumbnail.
 * 6 × 5s = 30s window to handle the WS race condition.
 */
const MAX_RACE_RETRIES = 6;
/** Skip polling assets whose lastUpdatedDate is older than 3 hours — likely stuck. */
const STALE_THRESHOLD_MS = 3 * 60 * 60 * 1_000;

function isStaleConverting(asset: Asset): boolean {
  if (!asset.lastUpdatedDate) return false;
  return Date.now() - new Date(asset.lastUpdatedDate).getTime() > STALE_THRESHOLD_MS;
}

/**
 * Polls assets every 5 seconds until thumbnails are available.
 *
 * Handles two scenarios:
 *
 * 1. **Explicit CONVERTING**: Asset is still `status: 'converting'` — polls
 *    until the backend updates it.
 *
 * 2. **Race condition (CONVERTING → ACTIVE, thumbnail: null)**: The backend
 *    fires a WS UPDATE before the thumbnail DB write completes. `batchFetchByIds`
 *    reads the intermediate state (`status: 'active', thumbnail: null`). No
 *    further WS event fires, so the blank placeholder is permanent. This poller
 *    tracks assets that were CONVERTING and re-polls them for up to 30 seconds
 *    after they transition to ACTIVE, until the thumbnail appears.
 */
export function useConvertingAssetPoller(assets: Asset[]): void {
  const client = useApolloClient();
  const { activeTeamId } = useTeamStore();

  // Always-fresh refs so the interval callback reads current values
  const assetsRef = useRef(assets);
  assetsRef.current = assets;

  const activeTeamIdRef = useRef(activeTeamId);
  activeTeamIdRef.current = activeTeamId;

  // id → remaining retries for race-condition victims (ACTIVE + no thumbnail)
  const raceWindowRef = useRef<Map<string, number>>(new Map());

  // Cleanup entries for assets no longer on this page
  const currentIds = new Set(assets.map((a) => a._id));
  for (const id of [...raceWindowRef.current.keys()]) {
    if (!currentIds.has(id)) raceWindowRef.current.delete(id);
  }

  // Update race window tracking based on current asset states
  assets.forEach((a) => {
    if (a.status === ASSET_STATUS.CONVERTING && !isStaleConverting(a)) {
      // Keep refreshed while converting so retries don't expire early
      raceWindowRef.current.set(a._id, MAX_RACE_RETRIES);
    } else if (a.thumbnail || a.status === ASSET_STATUS.FAILED || isStaleConverting(a)) {
      // Resolved — stop tracking
      raceWindowRef.current.delete(a._id);
    }
    // ACTIVE + no thumbnail: leave in map; retries count down in poll()
  });

  const hasConverting = assets.some(
    (a) => a.status === ASSET_STATUS.CONVERTING && !isStaleConverting(a),
  );
  const hasRaceWindow = raceWindowRef.current.size > 0;
  const needsPoll = hasConverting || hasRaceWindow;

  useEffect(() => {
    if (!needsPoll) return;

    const poll = async () => {
      const ids: string[] = [];

      assetsRef.current.forEach((a) => {
        if (a.status === ASSET_STATUS.CONVERTING && !isStaleConverting(a)) {
          ids.push(a._id);
        } else if (!a.thumbnail && raceWindowRef.current.has(a._id)) {
          const retries = raceWindowRef.current.get(a._id)!;
          if (retries > 0) {
            ids.push(a._id);
            raceWindowRef.current.set(a._id, retries - 1);
          } else {
            raceWindowRef.current.delete(a._id);
          }
        }
      });

      const teamId = activeTeamIdRef.current;
      if (ids.length === 0 || !teamId) return;

      try {
        await client.query({
          query: ASSETS_BY_IDS,
          variables: { ids, teamId },
          fetchPolicy: 'network-only',
        });
        // Apollo auto-merges Asset[] into normalized cache by __typename:_id
        // → AssetThumbnail re-renders once thumbnail field is populated
      } catch {
        // Silent — polling errors are non-fatal; WS subscription is still active
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [needsPoll, client]);
}
